<script setup lang="ts">
import { computed, onMounted, reactive, watch } from 'vue'
import dayjs from '@/lib/dayjs'

const LIST_MODE_OPTIONS = [
  { value: 'upcoming', text: 'Афиша' },
  { value: 'finished', text: 'Прошедшие вебинары' },
  { value: 'mine', text: 'Мои вебинары' },
  { value: 'liked', text: 'Избранное' }
]

const ACCESS_OPTIONS = [
  { value: 'p', text: 'Для родителей' },
  { value: 't', text: 'Для учителей' }
]

type Filters = Record<string, unknown>

const props = withDefaults(
  defineProps<{
    filters?: Filters
    query?: string
  }>(),
  {
    filters: () => ({}),
    query: ''
  }
)

const emit = defineEmits<{
  (e: 'update:filters', value: Filters): void
  (e: 'update:query', value: string): void
}>()

const listModeOptions = LIST_MODE_OPTIONS
const accessOptions = ACCESS_OPTIONS

const draft = reactive({
  list_mode: null as string | null,
  order: 'start_time',
  min_price: '' as string | number,
  max_price: '' as string | number,
  date_range: null as { start: unknown; end: unknown } | null,
  access_policy: [] as string[],
  has_video: false
})

const activeListMode = computed(() => {
  const current = props.filters || {}
  if (current.liked) return 'liked'
  if (current.purchased_or_organized) return 'mine'
  if (current.time_to && !current.time_from) return 'finished'
  if (current.time_from && !current.time_to) return 'upcoming'
  return null
})

const listModeIndex = computed({
  get() {
    const index = listModeOptions.findIndex(item => item.value === activeListMode.value)
    return index === -1 ? 0 : index
  },
  set(val: number) {
    const next = listModeOptions[val]
    if (!next) return
    setListMode(next.value)
  }
})

onMounted(() => {
  syncDraft()
  if (!activeListMode.value) setListMode('upcoming')
})

watch(
  () => props.filters,
  () => syncDraft(),
  { deep: true }
)

function syncDraft() {
  const current = props.filters || {}
  const accessPolicy =
    typeof current.access_policy === 'string'
      ? current.access_policy
          .split(',')
          .map(item => item.trim())
          .filter(Boolean)
      : []

  const dateRange =
    current.date_from && current.date_to
      ? { start: dayjs(String(current.date_from)), end: dayjs(String(current.date_to)) }
      : null

  draft.list_mode = activeListMode.value
  draft.order = String(current.order || 'start_time')
  draft.min_price = (current.min_price ?? '') as string | number
  draft.max_price = (current.max_price ?? '') as string | number
  draft.date_range = dateRange
  draft.access_policy = accessPolicy
  draft.has_video = current.has_video === true
}

function setListMode(mode: string) {
  const next = { ...(props.filters || {}) }
  const nextMode = activeListMode.value === mode ? null : mode
  applyListMode(next, nextMode)
  emit('update:filters', next)
}

function toggleSort(key: string) {
  const next = { ...(props.filters || {}) }
  const current = draft.order || 'start_time'
  const isSame = current.replace('-', '') === key
  const nextVal = isSame && !current.startsWith('-') ? `-${key}` : key
  draft.order = nextVal
  applyFields(next, { order: nextVal })
  emit('update:filters', next)
}

function sortIcon(key: string) {
  const current = draft.order || 'start_time'
  if (current.replace('-', '') !== key) return null
  return current.startsWith('-') ? 'mdi-sort-variant' : 'mdi-sort-reverse-variant'
}

function isSortActive(key: string) {
  return (draft.order || 'start_time').replace('-', '') === key
}

function sortMode(key: string) {
  return isSortActive(key) ? 'small flat primary' : 'small text primary'
}

function applyFilters() {
  const next = { ...(props.filters || {}) }
  applyListMode(next, draft.list_mode)
  applyFields(next, { order: draft.order })

  const minPrice = toNumber(draft.min_price)
  const maxPrice = toNumber(draft.max_price)
  applyFields(next, { min_price: minPrice, max_price: maxPrice })

  const hasDateRange = draft.date_range?.start && draft.date_range?.end
  if (draft.list_mode === 'upcoming' || draft.list_mode === 'finished' || !hasDateRange) {
    applyFields(next, { date_from: null, date_to: null })
  } else {
    const start = dayjs(draft.date_range!.start as string).format('YYYY-MM-DD')
    const end = dayjs(draft.date_range!.end as string).format('YYYY-MM-DD')
    applyFields(next, { date_from: start, date_to: end })
  }

  applyFields(next, { access_policy: draft.access_policy.join(',') || null })
  applyFields(next, { has_video: draft.has_video || null })
  emit('update:filters', next)
}

function clearFilters() {
  const next: Filters = {}
  if (props.filters?.order) next.order = props.filters.order
  applyListMode(next, activeListMode.value)
  emit('update:filters', next)
}

function applyListMode(target: Filters, mode: string | null) {
  applyFields(target, { liked: null, purchased_or_organized: null, time_from: null, time_to: null })

  if (mode === 'upcoming') {
    applyFields(target, { time_from: Math.floor(Date.now() / 1000), date_from: null, date_to: null })
    return
  }
  if (mode === 'finished') {
    applyFields(target, { time_to: Math.floor(Date.now() / 1000), date_from: null, date_to: null })
    return
  }
  if (mode === 'mine') {
    applyFields(target, { purchased_or_organized: true })
    return
  }
  if (mode === 'liked') {
    applyFields(target, { liked: true })
  }
}

function toggleAccess(value: string) {
  const next = Array.isArray(draft.access_policy) ? [...draft.access_policy] : []
  const idx = next.indexOf(value)
  if (idx === -1) next.push(value)
  else next.splice(idx, 1)
  draft.access_policy = next
  applyFilters()
}

function applyFields(obj: Filters, values: Filters) {
  Object.entries(values || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') delete obj[key]
    else obj[key] = value
  })
}

function toNumber(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}
</script>

<template>
  <div class="webinar-desktop-list-toolbar d-flex flex-column my-2">
    <div class="webinar-desktop-list-toolbar__card pa-3">
      <div class="webinar-desktop-list-toolbar__filters-row mb-2">
        <v-field label="Поиск">
           <v-input
             placeholder="Название вебинара или ФИО автора"
             :model-value="query"
             clearable
             @update:model-value="(val: string) => emit('update:query', val)"
             @submit="() => emit('update:query', query)"
           />
        </v-field>

        <v-field label="Стоимость" class="flex-spacer">
          <div class="d-flex">
             <v-input
               v-model="draft.min_price"
               class="flex-spacer"
               placeholder="от 100"
               type="number"
               @update:model-value="applyFilters"
             />
            <div class="mx-half" />
             <v-input
               v-model="draft.max_price"
               class="flex-spacer"
               placeholder="до 100 000"
               type="number"
               @update:model-value="applyFilters"
             />
          </div>
        </v-field>

        <v-field label="Дата проведения">
          <v-date-range-picker v-model="draft.date_range" style="height: 48px" @update:model-value="applyFilters" />
        </v-field>
      </div>

      <div class="webinar-desktop-list-toolbar__filters-row">
        <v-field label="Доступ" class="flex-spacer">
          <div class="webinar-desktop-list-toolbar__chips">
            <button
              v-for="option in accessOptions"
              :key="option.value"
              class="webinar-desktop-list-toolbar__chip"
              :class="{ 'webinar-desktop-list-toolbar__chip_active': draft.access_policy.includes(option.value) }"
              type="button"
              @click="() => toggleAccess(option.value)"
            >
              <span>{{ option.text }}</span>
              <v-icon
                v-if="draft.access_policy.includes(option.value)"
                class="webinar-desktop-list-toolbar__chip-icon"
                size="16"
              >
                mdi-close
              </v-icon>
            </button>
          </div>
        </v-field>

        <v-field>
          <template #label>&nbsp;</template>
          <div class="d-flex align-center flex-spacer">
            <v-checkbox v-model="draft.has_video" @update:model-value="applyFilters"> Есть видеозапись </v-checkbox>
          </div>
        </v-field>

        <v-field>
          <template #label>&nbsp;</template>
          <div class="d-flex align-center justify-end">
            <v-link mode="primary" class="mr-2 text-body-bold" @click="clearFilters"> Сбросить фильтры </v-link>
          </div>
        </v-field>
      </div>
    </div>

    <v-buttons-group v-model="listModeIndex" mode="inactive block" class="text-nowrap mt-2">
      <v-button v-for="(option, i) in listModeOptions" :key="option.value" type="button" mode="primary block">
        {{ option.text }}

        <v-link
          v-if="option.value === 'mine'"
          v-tooltip.bottom="
            'Вебинары, где вы участник или\nорганизатор, а также вебинары, по\nкоторым вы приобрели видеозаписи\nпосле их завершения.'
          "
          class="ml-1"
        >
          <v-icon size="20" :class="listModeIndex === i ? 'white-text' : 'gray-text'"> mdi-information-outline </v-icon>
        </v-link>
      </v-button>
    </v-buttons-group>

    <div class="d-flex align-center mt-2">
      <span class="mr-2">Сортировать</span>
      <v-button class="mr-2" :mode="sortMode('start_time')" @click="() => toggleSort('start_time')">
        по дате
        <v-icon v-if="sortIcon('start_time')" class="ml-1">{{ sortIcon('start_time') }}</v-icon>
      </v-button>
      <v-button :mode="sortMode('price')" @click="() => toggleSort('price')">
        по цене
        <v-icon v-if="sortIcon('price')" class="ml-1">{{ sortIcon('price') }}</v-icon>
      </v-button>
    </div>
  </div>
</template>

<style lang="scss">
.webinar-desktop-list-toolbar {
  &__card {
    background: $lightenGray;
    border-radius: 16px;
  }

  .v-field__label {
    font-weight: bold;
  }

  &__filters-row {
    display: grid;
    grid-auto-columns: 1fr;
    grid-auto-flow: column;
    gap: 16px;
  }

  &__chips {
    height: 48px;
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__chip {
    cursor: pointer;
    border: 1px solid $lightGray;
    background: white;
    color: $darkGray;
    border-radius: 999px;
    padding: 6px 12px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.15s ease;
    white-space: nowrap;
  }

  &__chip_active {
    background: $blue;
    color: white;
    border-color: $blue;
  }

  &__chip-icon {
    color: inherit;
  }

  @include mobile {
    display: none;
  }
}
</style>
